import React, { useContext } from 'react';

import {
  useListContext,
} from 'react-admin';

import {
  Box,
  Typography,
  CardContent,
  Divider,
  Card,
  useTheme,
  Chip,
  ButtonGroup,
  Button,
} from '@mui/material';
import { DateTime } from 'luxon';

import { useGetConfigurationSummaries } from '../transaction/TransactionSummaryTable';

import { requestTypes } from '../../constants/requestTypes';
import entityTypes from '../../constants/entityTypes';
import toStockholmTime from '../../utilities/toStockholmTime';
import { EntityContext } from '../../customFields/ReferenceEntityField';

const currencyMap: Record<string, {
  symbol: string;
  color: 'secondary' | 'blue' | 'yellow' | 'red' | 'orange';
} | undefined> = {
  USD: {
    symbol: '$',
    color: 'secondary',
  },
  EUR: {
    symbol: '€',
    color: 'blue',
  },
  JPY: {
    symbol: '¥',
    color: 'yellow',
  },
  GBP: {
    symbol: '£',
    color: 'blue',
  },
  CHF: {
    symbol: '₣',
    color: 'secondary',
  },
  RUB: {
    symbol: '₽',
    color: 'blue',
  },
  INR: {
    symbol: '₹',
    color: 'orange',
  },
  CNY: {
    symbol: '¥',
    color: 'red',
  },
  AUD: {
    symbol: 'A$',
    color: 'secondary',
  },
  CAD: {
    symbol: 'C$',
    color: 'red',
  },
  SGD: {
    symbol: 'S$',
    color: 'orange',
  },
  HKD: {
    symbol: 'HK$',
    color: 'yellow',
  },
  DKK: {
    symbol: 'kr',
    color: 'orange',
  },
  SEK: {
    symbol: 'kr',
    color: 'yellow',
  },
  NOK: {
    symbol: 'kr',
    color: 'red',
  },
};

const CurrencyKPICard = ({
  currency,
}: {
  currency: string;
}) => {
  const theme = useTheme();
  const entityContext = useContext(EntityContext);
  const [{
    data,
    isLoading,
  }] = useGetConfigurationSummaries([undefined], {
    from: toStockholmTime(DateTime.now()).minus({ years: 1 }).toISODate()!,
    to: toStockholmTime(DateTime.now()).toISODate()!,
    currency,
    entityId: entityContext?.id ?? '',
    entityType: entityContext?.type || entityTypes.BUSINESS,
    requestType: requestTypes.TRANSACTION,
  }, false);

  const {
    symbol,
    color,
  } = currencyMap[currency] || {
    symbol: '$',
    color: 'default',
  };

  if (isLoading || !data) return null;

  const volume12m = data.total.volume;
  const frequency12m = data.total.frequency;
  const netVolume12m = data.incoming.volume - data.outgoing.volume;
  const averageAmount12m = frequency12m === 0 ? 0 : volume12m / frequency12m;

  return (
    <Card
      sx={{
        backgroundColor: theme.palette.background.default,
        margin: 0,
      }}
    >
      <CardContent>
        <Box display="flex" alignItems="center" marginBottom={2} gap={2}>
          <Chip label={symbol} size="small" color={color} />
          <Typography>{currency}</Typography>
        </Box>
        <Box display="flex" flexDirection="column" gap={2}>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="label" fontSize={13}>Total volume (12m):</Typography>
            <Typography fontWeight={600}>{volume12m.toLocaleString(undefined, { currency, style: 'currency' })}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="label" fontSize={13}>Total transactions (12m):</Typography>
            <Typography fontWeight={600}>{frequency12m.toLocaleString(undefined)}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="label" fontSize={13}>Net volume (12m):</Typography>
            <Typography fontWeight={600}>{netVolume12m.toLocaleString(undefined, { currency, style: 'currency' })}</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between">
            <Typography variant="label" fontSize={13}>Average amount:</Typography>
            <Typography fontWeight={600}>{averageAmount12m.toLocaleString(undefined, { currency, style: 'currency' })}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const CurrencyCardKPIDatagrid = () => {
  const { data, isLoading } = useListContext();
  const [filterCurrency, setFilterCurrency] = React.useState<string>('all');
  const theme = useTheme();

  if (isLoading) return null;
  if (!data) return null;

  const filteredData = filterCurrency === 'all'
    ? data
    : data.filter((currency) => currency === filterCurrency);

  return (
    <Box marginTop={4}>
      <Divider />
      <Box marginTop={4}>
        <ButtonGroup variant="outlined" size="small" color="primary">
          {['all', ...data].map((currency) => (
            <Button
              key={currency}
              variant={filterCurrency === currency ? 'contained' : undefined}
              onClick={() => setFilterCurrency(currency)}
            >
              {currency === 'all' ? 'All' : currency}
            </Button>
          ))}
        </ButtonGroup>
      </Box>
      <Box marginTop={4} display="flex" flexDirection="column" gap={4}>
        {data.length > 0 ? filteredData.map((currency) => (
          <CurrencyKPICard key={currency} currency={currency} />
        )) : (
          <Card
            sx={{
              backgroundColor: theme.palette.background.default,
              margin: 0,
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="center">
                <Typography variant="body2">No transactions</Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default CurrencyCardKPIDatagrid;
